import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MarketDrivenStatusUpdateRequest_Status } from '../../../../../pb/requests/v1/market_driven_status_update_request_pb';
import { activateMarketDrivenShipping } from '../../api/activateMarketDrivenShipping';
import { MarketDrivenShippingMigrateConfirmModalContent } from './MarketDrivenShippingMigrateConfirmModalContent';

interface MarketDrivenShippingMigrateConfirmModalProps {
    id: string;
    modalRef: React.RefObject<HTMLElementTagNameMap['s-modal'] | null>;
    /** Called once the migration request has succeeded. */
    onConfirm: () => void;
}

/** Which error banner the modal shows after a failed migration request. */
type MigrationError = 'legacy' | 'generic';

// The "are you sure?" step before the real migration, shown from MarketDrivenShippingMigratedBanner's
// "Finish migration" button. Always mounted (see the banner's comment on why) — its content is only
// rendered once actually opened.
//
// "Migrate now" calls the migration endpoint and only hands over to `onConfirm` once it succeeds; a
// failure keeps the modal open with an error banner instead.
export const MarketDrivenShippingMigrateConfirmModal: React.FC<MarketDrivenShippingMigrateConfirmModalProps> = ({
    id,
    modalRef,
    onConfirm,
}) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isMigrating, setIsMigrating] = useState(false);
    const [error, setError] = useState<MigrationError | null>(null);

    const handleConfirm = () => {
        setIsMigrating(true);
        setError(null);

        activateMarketDrivenShipping(MarketDrivenStatusUpdateRequest_Status.ACTIVE)
            .then(() => onConfirm())
            .catch((requestError) => {
                console.error('Failed to activate market-driven shipping', requestError);
                // A LEGACY ecom is rejected with 400 — it has to be migrated first.
                setError(requestError?.response?.status === 400 ? 'legacy' : 'generic');
            })
            .finally(() => setIsMigrating(false));
    };

    return (
        <s-modal
            ref={modalRef}
            id={id}
            heading={t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.CONFIRM_MODAL.TITLE')}
            onShow={() => {
                setIsOpen(true);
                setError(null);
            }}
            onHide={() => setIsOpen(false)}
        >
            {isOpen && (
                <div className="flex flex-col gap-3">
                    {error && (
                        <s-banner
                            tone="critical"
                            heading={
                                error === 'legacy'
                                    ? t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.CONFIRM_MODAL.ERROR.LEGACY.TITLE')
                                    : t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.CONFIRM_MODAL.ERROR.GENERIC.TITLE')
                            }
                        >
                            <s-paragraph>
                                {error === 'legacy'
                                    ? t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.CONFIRM_MODAL.ERROR.LEGACY.DESCRIPTION')
                                    : t(
                                          'MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.CONFIRM_MODAL.ERROR.GENERIC.DESCRIPTION'
                                      )}
                            </s-paragraph>
                        </s-banner>
                    )}
                    <MarketDrivenShippingMigrateConfirmModalContent />
                </div>
            )}
            <s-button
                slot="primary-action"
                variant="primary"
                loading={isMigrating || undefined}
                onClick={handleConfirm}
            >
                {t('MARKET_DRIVEN_SHIPPING_MIGRATED_BANNER.CONFIRM_MODAL.CONFIRM_BUTTON')}
            </s-button>
            <s-button slot="secondary-actions" commandFor={id} command="--hide" disabled={isMigrating || undefined}>
                {t('BUTTON.CANCEL')}
            </s-button>
        </s-modal>
    );
};

export default MarketDrivenShippingMigrateConfirmModal;
